import { useQueryParam } from "@/hooks/use-query-param";
import { formatNumberToDisplay } from "@/lib/api/common";
import { generateDisplayLabelForOtcDetailedType } from "./common";
import { getRiskColor } from "./colorPalette";

export function PreviewDisplayCard({ detailViewToRender, commodityData, otcInfo, enableClick, nameMetaAppendix }: { detailViewToRender: string; commodityData: any; otcInfo?: any; enableClick?: boolean; nameMetaAppendix?: string; }) {
    const [_, setDetailView] = useQueryParam("detail_view", "");

    const formatQuantity = (value: any) => formatNumberToDisplay(value, { emptyFormat: '--', zeroFormat: '--' });
    const formatPercent = (value: any) => formatNumberToDisplay(value, { emptyFormat: '0 %', zeroFormat: '0 %', suffix: "%" });

    const onClick = enableClick ? () => setDetailView(detailViewToRender) : undefined;
    const clickClassName = enableClick ? "cursor-pointer" : "";

    if (detailViewToRender.startsWith("otc_") && otcInfo) {
        const [otcType, stats] = otcInfo;
        return (
            <div key={otcType} className={`bg-slate-50 border border-slate-200 rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                <div className="text-slate-700 font-medium text-sm mb-1">
                    {generateDisplayLabelForOtcDetailedType(otcType)}
                </div>
                <div className="text-slate-900 font-bold text-xl">
                    {formatQuantity(stats?.quantity || 0)}
                </div>
                {stats?.percent && (
                    <div className="text-slate-600 text-sm mt-1">
                        {formatPercent(stats.percent)}
                    </div>
                )}
            </div>
        )
    }

    const oddityColors = getRiskColor('dp');
    const beaconColors = getRiskColor('beacon');

    switch (detailViewToRender) {
        case "none":
            const noneColors = getRiskColor('offers');
            return (
                <div className={`${noneColors.background} border ${noneColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${noneColors.text} font-medium text-sm mb-1 flex justify-between`}>{nameMetaAppendix ?? ""} <span>{formatPercent(0)}</span></div>
                    <div className={`${noneColors.textBold} font-bold text-xl`}>{formatQuantity(0)}</div>
                </div>
            );
        case "open_offers":
            const openOffersColors = getRiskColor('offers');
            return (
                <div className={`${openOffersColors.background} border ${openOffersColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${openOffersColors.text} font-medium text-sm mb-1 flex justify-between`}>Open Offers | Total {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.percentOverProjectedProduction(commodityData.open_offer_stats))}</span></div>
                    <div className={`${openOffersColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.open_offer_stats)}</div>
                </div>
            );
        case "base_open_offers":
            const baseOffersColors = getRiskColor('offers');
            return (
                <div className={`${baseOffersColors.background} border ${baseOffersColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${baseOffersColors.text} font-medium text-sm mb-1 flex justify-between`}>Open Offers {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.percentOverProjectedProduction(commodityData.base_open_offer_stats))}</span></div>
                    <div className={`${baseOffersColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.base_open_offer_stats)}</div>
                </div>
            );
        case "manual_open_offers":
            const manualOffersColors = getRiskColor('offers');
            return (
                <div className={`${manualOffersColors.background} border ${manualOffersColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${manualOffersColors.text} font-medium text-sm mb-1 flex justify-between`}>Manual Open Offers {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.percentOverProjectedProduction(commodityData.manual_open_offer_stats))}</span></div>
                    <div className={`${manualOffersColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.manual_open_offer_stats)}</div>
                </div>
            );
        case "manual_otc_offer_ups":
            const manualOtcOffersColors = getRiskColor('offers');
            return (
                <div className={`${manualOtcOffersColors.background} border ${manualOtcOffersColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${manualOtcOffersColors.text} font-medium text-sm mb-1 flex justify-between`}>OTC Offer Ups {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.percentOverProjectedProduction(commodityData.manual_otc_offer_up_stats))}</span></div>
                    <div className={`${manualOtcOffersColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.manual_otc_offer_up_stats)}</div>
                </div>
            );
        case "cash":
            const cashColors = getRiskColor('cash');
            return (
                <div className={`${cashColors.background} border ${cashColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${cashColors.text} font-medium text-sm mb-1 flex justify-between`}>Cash {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.cash_percent)}</span></div>
                    <div className={`${cashColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.cash_quantity)}</div>
                </div>
            );
        case "basis":
            const basisColors = getRiskColor('basis');
            return (
                <div className={`${basisColors.background} border ${basisColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${basisColors.text} font-medium text-sm mb-1 flex justify-between`}>Basis {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.basis_percent)}</span></div>
                    <div className={`${basisColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.basis_quantity)}</div>
                </div>
            );
        case "open_brokerage":
            const brokerageColors = getRiskColor('brokerage');
            return (
                <div className={`${brokerageColors.background} border ${brokerageColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${brokerageColors.text} font-medium text-sm mb-1 flex justify-between`}>Brokerage {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.open_brokerage_percent)}</span></div>
                    <div className={`${brokerageColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.open_brokerage_quantity)}</div>
                </div>
            );
        case "hta":
            const htaColors = getRiskColor('hta');
            return (
                <div className={`${htaColors.background} border ${htaColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${htaColors.text} font-medium text-sm mb-1 flex justify-between`}>HTA {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.hta_percent)}</span></div>
                    <div className={`${htaColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.hta_quantity)}</div>
                </div>
            );
        case "beacon":
            return (
                <div className={`${beaconColors.background} border ${beaconColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${beaconColors.text} font-medium text-sm mb-1 flex justify-between`}>Beacon {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.beacon_percent)}</span></div>
                    <div className={`${beaconColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.beacon_quantity)}</div>
                </div>
            )
        case 'otc':
            const otcColors = getRiskColor('otc');
            return (
                <div className={`${otcColors.background} border ${otcColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${otcColors.text} font-medium text-sm mb-1 flex justify-between`}>OTC {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.double_up_percent)}</span></div>
                    <div className={`${otcColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.double_up_quantity)}</div>
                </div>
            )
        case 'otc-futures':
            const otcFuturesColors = getRiskColor('otc');
            return (
                <div className={`${otcFuturesColors.background} border ${otcFuturesColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${otcFuturesColors.text} font-medium text-sm mb-1 flex justify-between`}>Futures OTC {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.otc_futures_percent)}</span></div>
                    <div className={`${otcFuturesColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.otc_futures_quantity)}</div>
                </div>
            )
        case 'dpq-futures':
            const dpqFuturesColors = getRiskColor('dpq');
            return (
                <div className={`${dpqFuturesColors.background} border ${dpqFuturesColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${dpqFuturesColors.text} font-medium text-sm mb-1 flex justify-between`}>DPQ {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.dpq_futures_percent)}</span></div>
                    <div className={`${dpqFuturesColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.dpq_futures_quantity)}</div>
                </div>
            )
        case 'dpq-basis':
            const dpqBasisColors = getRiskColor('dpq');
            return (
                <div className={`${dpqBasisColors.background} border ${dpqBasisColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${dpqBasisColors.text} font-medium text-sm mb-1 flex justify-between`}>DPQ {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.dpq_basis_percent)}</span></div>
                    <div className={`${dpqBasisColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.dpq_basis_quantity)}</div>
                </div>
            )
        case 'otc-beacon-basis':
            const otcBeaconBasisColors = getRiskColor('otc');
            return (
                <div className={`${otcBeaconBasisColors.background} border ${otcBeaconBasisColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${otcBeaconBasisColors.text} font-medium text-sm mb-1 flex justify-between`}>OTC/Beacon {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.otc_beacon_basis_percent)}</span></div>
                    <div className={`${otcBeaconBasisColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.otc_beacon_basis_quantity)}</div>
                </div>
            )
        case 'otc-basis':
            const otcBasisColors = getRiskColor('otc');
            return (
                <div className={`${otcBasisColors.background} border ${otcBasisColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${otcBasisColors.text} font-medium text-sm mb-1 flex justify-between`}>OTC {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.otc_basis_percent)}</span></div>
                    <div className={`${otcBasisColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.otc_basis_quantity)}</div>
                </div>
            )
        case 'applicable_dp':
            const applicableDpColors = getRiskColor('dp');
            return (
                <div className={`${applicableDpColors.background} border ${applicableDpColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${applicableDpColors.text} font-medium text-sm mb-1 flex justify-between`}>Applicable DP {nameMetaAppendix ?? ""} <span>{formatPercent(commodityData.applicable_dp_percent)}</span></div>
                    <div className={`${applicableDpColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.applicable_dp_quantity)}</div>
                </div>
            )
        case 'non_applicable_dp':
            const nonApplicableDpColors = getRiskColor('dp');
            return (
                <div className={`${nonApplicableDpColors.background} border ${nonApplicableDpColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${nonApplicableDpColors.text} font-medium text-sm mb-1 flex justify-between`}>DP {nameMetaAppendix ?? ""}<span>{formatPercent(commodityData.non_applicable_dp_percent)}</span></div>
                    <div className={`${nonApplicableDpColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.non_applicable_dp_quantity)}</div>
                </div>
            )
        case 'direct-sales-non-appl':
            return (
                <div className={`${oddityColors.background} border ${oddityColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${oddityColors.text} font-medium text-sm mb-1 flex justify-between`}>Direct Sales {nameMetaAppendix ?? ""}<span>{formatPercent(commodityData.percentOverProjectedProduction(commodityData.direct_sales_starting_quantity))}</span></div>
                    <div className={`${oddityColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.direct_sales_starting_quantity)}</div>
                </div>
            )
        case 'direct-sales-basis':
            return (
                <div className={`${oddityColors.background} border ${oddityColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${oddityColors.text} font-medium text-sm mb-1 flex justify-between`}>Direct Sales {nameMetaAppendix ?? ""}<span>{formatPercent(commodityData.percentOverProjectedProduction(commodityData.direct_sales_basis_quantity))}</span></div>
                    <div className={`${oddityColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.direct_sales_basis_quantity)}</div>
                </div>
            )
        case 'direct-sales-futures':
            return (
                <div className={`${oddityColors.background} border ${oddityColors.border} rounded-lg p-4 ${clickClassName}`} onClick={onClick}>
                    <div className={`${oddityColors.text} font-medium text-sm mb-1 flex justify-between`}>Direct Sales {nameMetaAppendix ?? ""}<span>{formatPercent(commodityData.percentOverProjectedProduction(commodityData.direct_sales_futures_quantity))}</span></div>
                    <div className={`${oddityColors.textBold} font-bold text-xl`}>{formatQuantity(commodityData.direct_sales_futures_quantity)}</div>
                </div>
            )
        default:
            return null;
    }

    return null;
}
